import { useMemo } from 'react'
import { Table, Tbody, Text, Th, Thead, Tr, Td, Box } from '@chakra-ui/react'
import AllergyHistoryRow from './AllergyHistoryRow'
import FirestoreUser, {
  AllergyData,
} from '../../../../../../types/FirestoreUser'
import { calculateAge } from '../../../../../../utils/calculateAge'

interface Props {
  uid: string
  firestoreUser: FirestoreUser
  allergyHistory: AllergyData[]
  setAllergyHistory: (data: AllergyData[]) => void
}

interface AllergySectionProps {
  title: string
  allergies: { name: string }[]
  userAge: number | undefined
  uid: string
  allergyHistory: AllergyData[]
  setAllergyHistory: (data: AllergyData[]) => void
}

function AllergySection({
  title,
  allergies,
  userAge,
  uid,
  allergyHistory,
  setAllergyHistory,
}: AllergySectionProps) {
  return (
    <>
      <Tr>
        <Td colSpan={6}>
          <Text fontWeight='bold'>{title}</Text>
        </Td>
      </Tr>
      {allergies
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((allergy) => (
          <AllergyHistoryRow
            key={allergy.name}
            allergy={allergy.name}
            age={userAge}
            uid={uid}
            allergyHistory={allergyHistory}
            setAllergyHistory={setAllergyHistory}
          />
        ))}
    </>
  )
}

const commonAllergies = [
  { name: 'Peanuts' },
  { name: 'Tree nuts (almonds, cashews, walnuts)' },
  { name: 'Milk/Dairy' },
  { name: 'Eggs' },
  { name: 'Wheat/Gluten' },
  { name: 'Soy' },
  { name: 'Fish' },
  { name: 'Shellfish' },
  { name: 'Sesame' },
  { name: 'Penicillin' },
  { name: 'Sulfa drugs' },
  { name: 'Aspirin' },
  { name: 'Ibuprofen' },
  { name: 'Pollen (hay fever)' },
  { name: 'Dust mites' },
  { name: 'Mold' },
  { name: 'Pet dander (cats, dogs)' },
  { name: 'Insect stings (bees, wasps)' },
  { name: 'Latex' },
  { name: 'Nickel' },
]

export default function AllergyHistory({
  uid,
  firestoreUser,
  allergyHistory,
  setAllergyHistory,
}: Props) {
  const userAge = useMemo(() => {
    const birthDate = firestoreUser.profile?.birthDate
    if (!birthDate) return undefined
    return calculateAge(birthDate)
  }, [firestoreUser])

  return (
    <>
      <Box overflowX='auto'>
        <Table variant='simple' mt={4}>
          <Thead>
            <Tr>
              <Th textAlign='left'>Allergy</Th>
              <Th textAlign='left'>Have/Had Allergy</Th>
              <Th textAlign='left'>Age Diagnosed</Th>
              <Th textAlign='left'>Age Ended (Blank if current)</Th>
              <Th textAlign='left'>Severity</Th>
              <Th textAlign='left'>Estimated total years</Th>
            </Tr>
          </Thead>
          <Tbody>
            <AllergySection
              title='Common Allergies'
              allergies={commonAllergies}
              userAge={userAge}
              uid={uid}
              allergyHistory={allergyHistory}
              setAllergyHistory={setAllergyHistory}
            />
          </Tbody>
        </Table>
      </Box>
    </>
  )
}
